import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';

export interface Profile {
  id: string;
  name: string;
  active: boolean;
  locked_control_code: string[];
  created_at: string;
}

export interface CreateProfileDTO {
  name: string;
  active?: boolean;
}

export interface UpdateProfileDTO {
  name?: string;
  active?: boolean;
}

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface ProfileQueryParams {
  page?: number;
  limit?: number;
  name?: string;
  active?: boolean;
  locked_control_code_regex?: string;
}

export interface SetLockedControlDTO {
  locked_control_code: string[];
  operation: 'add' | 'remove';
  control_name: string;
}

export const profileApi = {
  findMany: async (params?: ProfileQueryParams): Promise<PaginatedResponse<Profile>> => {
    const { data } = await api.get<ApiPaginatedResponse<Profile>>('/admin/profiles', { params });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  findOne: async (id: string): Promise<Profile> => {
    const { data } = await api.get<ApiResponse<Profile>>(`/admin/profiles/${id}`);
    return data.data;
  },

  create: async (dto: CreateProfileDTO): Promise<Profile> => {
    const { data } = await api.post<ApiResponse<Profile>>('/admin/profiles', dto);
    return data.data;
  },

  update: async (id: string, dto: UpdateProfileDTO): Promise<Profile> => {
    const { data } = await api.patch<ApiResponse<Profile>>(`/admin/profiles/${id}`, dto);
    return data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/profiles/${id}`);
  },

  setActive: async (
    id: string,
    active: boolean,
  ): Promise<{ success: boolean; active: boolean }> => {
    const { data } = await api.post<ApiResponse<{ success: boolean; active: boolean }>>(
      `/admin/profiles/${id}/active`,
      { active },
    );
    return data.data;
  },

  setLockedControls: async (
    id: string,
    dto: SetLockedControlDTO,
  ): Promise<{ success: boolean }> => {
    const { data } = await api.put<ApiResponse<{ success: boolean }>>(
      `/admin/profiles/${id}/controls`,
      dto,
    );
    return data.data;
  },

  // Find profiles that have a specific control code in their locked_control_code array
  findByControlCode: async (controlCode: string): Promise<PaginatedResponse<Profile>> => {
    const { data } = await api.get<ApiPaginatedResponse<Profile>>('/admin/profiles', {
      params: { locked_control_code_regex: controlCode },
    });
    return {
      docs: data.data,
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },
};
